import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Box,
  Chip,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Email as EmailIcon,
  Subject as SubjectIcon,
  Message as MessageIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

const EmailFormCard = () => {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:phosaico@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`From: ${email}\n\n${body}`)}`;
    window.open(mailtoLink, '_blank');
    setEmail('');
    setSubject('');
    setBody('');
  };

  const contactInfo = [
    {
      icon: <BusinessIcon />,
      label: 'Makerere University',
      value: 'Kampala, Uganda',
      color: '#F59E0B'
    },
    {
      icon: <LocationIcon />,
      label: 'Headquarters',
      value: 'East Africa',
      color: '#F59E0B'
    },
    {
      icon: <PhoneIcon />,
      label: 'Phone',
      value: '+256 750 371 313',
      color: '#F59E0B'
    }
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 500, margin: '0 auto' }}>
      <Card sx={{
        borderRadius: '24px',
        overflow: 'hidden',
        background: 'white',
        border: '1px solid rgba(245,158,11, 0.08)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 30px 60px rgba(245,158,11, 0.15)',
          borderColor: 'rgba(245,158,11, 0.2)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #F59E0B, #D97706, #F59E0B)',
          zIndex: 1,
        }
      }}>
        <CardContent sx={{ p: { xs: 4, md: 6 } }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Chip
              icon={<EmailIcon />}
              label="GET IN TOUCH"
              sx={{
                backgroundColor: 'rgba(245,158,11, 0.1)',
                color: '#F59E0B',
                fontWeight: 700,
                fontSize: '0.8rem',
                mb: 3,
                px: 3,
                py: 1.5,
                borderRadius: '20px',
                '& .MuiChip-icon': { color: '#F59E0B' }
              }}
            />
            
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 900,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                mb: 2,
                color: '#111827',
              }}
            >
              Let's Work Together
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '1rem',
                lineHeight: 1.6,
                color: '#6B7280',
                fontWeight: 500,
              }}
            >
              Ready to transform your business with AI-powered language solutions?
            </Typography>
          </Box>

          {/* Contact Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 5 }}>
            <TextField
              label="Email Address"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ color: '#F59E0B', fontSize: 20 }}>
                      <EmailIcon />
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  backgroundColor: 'rgba(245,158,11, 0.02)',
                  border: '1px solid rgba(245,158,11, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(245,158,11, 0.05)',
                    borderColor: 'rgba(245,158,11, 0.3)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(245,158,11, 0.08)',
                    borderColor: '#F59E0B',
                    boxShadow: '0 0 0 3px rgba(245,158,11, 0.1)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#6B7280',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#F59E0B',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '16px 16px 16px 48px',
                  fontSize: '1rem',
                  fontWeight: 500,
                },
              }}
            />
            
            <TextField
              label="Subject"
              variant="outlined"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ color: '#F59E0B', fontSize: 20 }}>
                      <SubjectIcon />
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  backgroundColor: 'rgba(245,158,11, 0.02)',
                  border: '1px solid rgba(245,158,11, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(245,158,11, 0.05)',
                    borderColor: 'rgba(245,158,11, 0.3)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(245,158,11, 0.08)',
                    borderColor: '#F59E0B',
                    boxShadow: '0 0 0 3px rgba(245,158,11, 0.1)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#6B7280',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#F59E0B',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '16px 16px 16px 48px',
                  fontSize: '1rem',
                  fontWeight: 500,
                },
              }}
            />
            
            <TextField
              label="Message"
              variant="outlined"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              rows={4}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 2 }}>
                    <Box sx={{ color: '#F59E0B', fontSize: 20 }}>
                      <MessageIcon />
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  backgroundColor: 'rgba(245,158,11, 0.02)',
                  border: '1px solid rgba(245,158,11, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(245,158,11, 0.05)',
                    borderColor: 'rgba(245,158,11, 0.3)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(245,158,11, 0.08)',
                    borderColor: '#F59E0B',
                    boxShadow: '0 0 0 3px rgba(245,158,11, 0.1)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#6B7280',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#F59E0B',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '16px 16px 16px 48px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: 1.6,
                },
              }}
            />
            
            <Button 
              variant="contained" 
              type="submit" 
              fullWidth
              endIcon={<SendIcon />}
              sx={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                color: 'white',
                mt: 4,
                py: 2,
                borderRadius: '20px',
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 8px 25px rgba(245,158,11, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 15px 35px rgba(245,158,11, 0.4)',
                  background: 'linear-gradient(135deg, #D97706 0%, #6D28D9 100%)',
                },
              }}
            >
              Send Message
            </Button>
          </Box>

          {/* Contact Information */}
          <Box sx={{
            p: 4,
            borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(245,158,11, 0.05) 0%, rgba(124, 58, 237, 0.02) 100%)',
            border: '1px solid rgba(245,158,11, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11, 0.1) 0%, transparent 70%)',
              zIndex: 0,
            }
          }}>
            <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 800,
                  color: '#111827',
                  mb: 2,
                  fontSize: '1.25rem',
                }}
              >
                Contact Information
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#6B7280',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}
              >
                Get in touch with our team
              </Typography>
            </Box>

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {contactInfo.map((info, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    p: 2,
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: `1px solid ${info.color}20`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(8px)',
                      boxShadow: `0 8px 25px ${info.color}20`,
                      background: 'white',
                    }
                  }}
                >
                  <Box sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor: `${info.color}15`,
                    color: info.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}>
                    {info.icon}
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        mb: 0.5,
                      }}
                    >
                      {info.label}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#111827',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      {info.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailFormCard;
